// Matter types
// NOTE: the legacy `status` (NEW/IN_PROGRESS/CLOSED) is a dead DB column —
// the Case 360° `phase` is the only lifecycle field the app uses.
export type MatterResult = 'WIN' | 'LOSE' | 'SETTLED' | 'CANCELLED';
export type ClientStatus = 'DEFENDANT' | 'PLAINTIFF' | 'SUSPECT' | 'ACCUSED' | 'WITNESS';

// Case 360° (Lot 2) lifecycle — see lot2 stage-00/stage-01.
export type MatterPhase =
  | 'INTAKE'
  | 'PROVISIONAL_OPEN'
  | 'PLANNING'
  | 'EXECUTION'
  | 'REVIEW'
  | 'CLOSED'
  | 'SUSPENDED';

// Case 360° (Lot 2) Stage 13 — billing posture derived from the phase.
export type BillingStatus = 'allowed' | 'tracking_only' | 'readonly';

// Case 360° forward-only legal path (mirrors the backend PhaseService).
export const NEXT_PHASE: Partial<Record<MatterPhase, MatterPhase>> = {
  INTAKE: 'PLANNING',
  PLANNING: 'EXECUTION',
  EXECUTION: 'REVIEW',
  REVIEW: 'CLOSED',
};

export type MatterCategory =
  | 'CC'
  | 'CDC'
  | 'ADC'
  | 'ECD'
  | 'CLA'
  | 'PW'
  | 'EBC'
  | 'QA'
  | 'MIGRATION_PENDING';

// The 8 selectable categories (MIGRATION_PENDING is legacy-only, never chosen).
export const MATTER_CATEGORY_VALUES: Exclude<MatterCategory, 'MIGRATION_PENDING'>[] = [
  'CC',
  'CDC',
  'ADC',
  'ECD',
  'CLA',
  'PW',
  'EBC',
  'QA',
];

// Case 360° (Lot 2) Stage 6 — Compliance & Risk.
export type ComplianceStatus =
  | 'PENDING'
  | 'PASSED'
  | 'PASSED_WITH_CONDITIONS'
  | 'FAILED'
  | 'BYPASSED';

export type RiskLevel = 'LOW' | 'MEDIUM' | 'HIGH';

export const COMPLIANCE_STATUS_VALUES: ComplianceStatus[] = [
  'PENDING',
  'PASSED',
  'PASSED_WITH_CONDITIONS',
  'FAILED',
  'BYPASSED',
];

export const RISK_LEVEL_VALUES: RiskLevel[] = ['LOW', 'MEDIUM', 'HIGH'];

// Case 360° (Lot 2) Stage 5 — SUSPENDED exit paths (NORMAL = regular close).
export type ClosureType =
  | 'NORMAL'
  | 'CONFLICT_TRANSFER'
  | 'CLIENT_WAIVER'
  | 'ADMINISTRATIVE';

// The selectable archive paths from SUSPENDED (NORMAL is rejected there).
export const SUSPENDED_CLOSURE_TYPES: Exclude<ClosureType, 'NORMAL'>[] = [
  'CONFLICT_TRANSFER',
  'CLIENT_WAIVER',
  'ADMINISTRATIVE',
];

export interface CloseSuspendedDto {
  closureType: Exclude<ClosureType, 'NORMAL'>;
  closureReason: string;
}

// Case 360° (Lot 2) Stage 7 — Interaction Log.
export type InteractionType =
  | 'COURT_HEARING'
  | 'MEETING'
  | 'CALL'
  | 'EMAIL'
  | 'OTHER';

export const INTERACTION_TYPE_VALUES: InteractionType[] = [
  'COURT_HEARING',
  'MEETING',
  'CALL',
  'EMAIL',
  'OTHER',
];

export interface InteractionEntry {
  id: number;
  matterId: number;
  type: InteractionType;
  date: string;
  summary?: string | null | undefined;
  isDraft: boolean;
  authorId: number;
  eventId?: number | null | undefined;
  createdAt: string;
  author?: { id: number; name?: string | null } | null | undefined;
}

export interface CreateInteractionDto {
  type: InteractionType;
  date?: string | undefined;
  summary: string;
}

export interface UpdateInteractionDto {
  type?: InteractionType | undefined;
  date?: string | undefined;
  summary?: string | undefined;
}

// Case 360° (Lot 2) Stage 9 — Court Instances & Appeal Deadlines.
export type InstanceType =
  | 'INVESTIGATION'
  | 'FIRST'
  | 'APPEAL'
  | 'CASSATION'
  | 'SUPERVISION'
  | 'REMANDED';

export type DateBasis =
  | 'DECISION_DATE'
  | 'RECEIPT_DATE'
  | 'SERVICE_DATE'
  | 'EFFECTIVE_DATE'
  | 'MANUAL';

export const INSTANCE_TYPE_VALUES: InstanceType[] = [
  'INVESTIGATION',
  'FIRST',
  'APPEAL',
  'CASSATION',
  'SUPERVISION',
  'REMANDED',
];

export const DATE_BASIS_VALUES: DateBasis[] = [
  'DECISION_DATE',
  'RECEIPT_DATE',
  'SERVICE_DATE',
  'EFFECTIVE_DATE',
  'MANUAL',
];

export interface CourtInstance {
  id: number;
  matterId: number;
  type: InstanceType;
  instanceNumber: number;
  isCurrent: boolean;
  dateBasis: DateBasis;
  baseDate?: string | null | undefined;
  appealDeadline?: string | null | undefined;
  leoCourtId?: number | null | undefined;
  leoCourt?: { id: number; name: string; city: string } | null | undefined;
  createdAt: string;
  updatedAt?: string | undefined;
}

export interface CreateCourtInstanceDto {
  type: InstanceType;
  instanceNumber?: number | undefined;
  dateBasis: DateBasis;
  baseDate?: string | undefined;
  appealDeadline?: string | undefined;
  leoCourtId?: number | undefined;
  isCurrent?: boolean | undefined;
}

export interface UpdateCourtInstanceDto extends Partial<CreateCourtInstanceDto> {
  /** Required when changing an already-set appeal deadline. */
  overrideReason?: string | undefined;
}

// Case Instances — named work engagements on INSTANCE-billed matters.
export interface CaseInstance {
  id: number;
  matterId: number;
  name: string;
  startedAt: string;
  /** null = active/running. */
  stoppedAt?: string | null | undefined;
  result?: string | null | undefined;
  createdById: number;
  createdAt: string;
  updatedAt?: string | undefined;
}

export interface StartCaseInstanceDto {
  name: string;
}

export interface StopCaseInstanceDto {
  result: string;
}

export interface UpdateCaseInstanceDto {
  name?: string | undefined;
  result?: string | undefined;
}

// Case 360° (Lot 2) Stage 10 — Documents (versioning, SECRET, break-glass).
export interface DocumentFile {
  id: number;
  originalName: string;
  path: string;
  size?: number | undefined;
}

export interface DocumentVersion {
  id: number;
  documentId: number;
  version: number;
  fileId: number;
  file?: DocumentFile | undefined;
  author?: { id: number; name: string } | null | undefined;
  createdAt: string;
}

export interface MatterDocument {
  id: number;
  name: string;
  matterId: number;
  isSecret: boolean;
  /** The matter's engagement letter (at most one active per matter). */
  isEngagementLetter?: boolean | undefined;
  /** true when the caller may not read this SECRET document. */
  locked?: boolean | undefined;
  typeId?: number | null | undefined;
  type?: { id: number; name: string } | null | undefined;
  currentVersionId?: number | null | undefined;
  currentVersion?: (DocumentVersion & { file?: DocumentFile | undefined }) | null | undefined;
  versions?: DocumentVersion[] | undefined;
  _count?: { versions: number } | undefined;
  breakGlassExpiresAt?: string | undefined;
  createdById?: number | null | undefined;
  createdAt: string;
  updatedAt?: string | undefined;
}

export interface CreateDocumentDto {
  matterId: number;
  file: File;
  name?: string | undefined;
  isSecret?: boolean | undefined;
  isEngagementLetter?: boolean | undefined;
}

export interface UpdateDocumentDto {
  name?: string | undefined;
  isSecret?: boolean | undefined;
}

export interface BreakGlassRequestDto {
  reason: string;
}

export interface GrantBreakGlassDto {
  auditorId: number;
  reason: string;
}

// Case 360° (Lot 2) Stage 11 — Matter Plan & Change Requests.
export type CrType = 'SCOPE' | 'BUDGET' | 'TIMELINE' | 'TEAM';
export type CrStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export const CR_TYPE_VALUES: CrType[] = ['SCOPE', 'BUDGET', 'TIMELINE', 'TEAM'];

export interface ChangeRequest {
  id: number;
  matterId: number;
  type: CrType;
  /** Requested new value (JSON string for SCOPE/TEAM). */
  targetValue: string;
  /** Snapshot at creation (JSON string), for the diff. */
  previousValue?: string | null | undefined;
  reason: string;
  status: CrStatus;
  rejectReason?: string | null | undefined;
  authorId: number;
  author?: { id: number; name?: string | null } | null | undefined;
  approverId?: number | null | undefined;
  approver?: { id: number; name?: string | null } | null | undefined;
  decidedAt?: string | null | undefined;
  createdAt: string;
}

export interface CreateChangeRequestDto {
  type: CrType;
  targetValue: string;
  reason: string;
}

export interface UpdateMatterPlanDto {
  scopeIn?: string | undefined;
  scopeOut?: string | undefined;
  estimate?: number | undefined;
  budget?: number | undefined;
  targetEndDate?: string | undefined;
}

export interface MatterPlan {
  id: number;
  scopeIn?: string | null | undefined;
  scopeOut?: string | null | undefined;
  estimate?: number | null | undefined;
  budget?: number | null | undefined;
  targetEndDate?: string | null | undefined;
  matterPlanConfirmed: boolean;
  feedbackRequested: boolean;
}

export interface BypassRateInfo {
  rate: number; // 0..1, firm-wide over the rolling window
  bypassed: number;
  total: number;
  level: 'ok' | 'yellow' | 'red';
  mdOverride: boolean;
  blocked: boolean;
}

export interface ComplianceState {
  id: number;
  phase?: MatterPhase | undefined;
  compliance: ComplianceStatus;
  complianceNote?: string | null | undefined;
  riskLevel?: RiskLevel | null | undefined;
  bypass: BypassRateInfo;
}

export interface SetComplianceDto {
  status: ComplianceStatus;
  note?: string | undefined;
  riskLevel?: RiskLevel | undefined;
}

export interface SetRiskLevelDto {
  riskLevel: RiskLevel;
}

// Quality-Gate check shapes (mirror the backend PhaseService).
export type GateSeverity = 'hard' | 'soft';
export type GateBlockStatus = 'pass' | 'fail' | 'pending';

export interface GateBlock {
  key: string;
  label: string;
  severity: GateSeverity;
  status: GateBlockStatus;
  fill?: 'inline' | 'deeplink' | undefined;
  target?: string | undefined;
  note?: string | undefined;
}

export interface GateOpenTimer {
  id: number;
  userId: number;
  description: string;
}

export interface GateCheckResult {
  matterId: number;
  fromPhase: MatterPhase;
  toPhase: MatterPhase | null;
  gate: 1 | 2 | 3 | 4 | null;
  blocks: GateBlock[];
  softFails: GateBlock[];
  hardFails: GateBlock[];
  canProceed: boolean;
  openTimers?: GateOpenTimer[] | undefined;
}

export interface TransitionMatterDto {
  toPhase: MatterPhase;
  softOverrideReason?: string | undefined;
}

// Case 360° (Lot 2) Stage 2 — LACI Lead.
export interface LeadDelegation {
  id: number;
  matterId: number;
  fromUserId: number;
  toUserId: number;
  acknowledged: boolean;
  forced: boolean;
  createdAt: string;
}

export interface LeadInfo {
  matterId: number;
  lead?: { id: number; name?: string | null; roleType?: string | null } | null | undefined;
  leads?: { id: number; name?: string | null; roleType?: string | null }[] | undefined;
  pendingDelegation?: LeadDelegation | null | undefined;
}

export interface MatterUser {
  id: number;
  name: string;
  lastname?: string | undefined;
  email?: string | undefined;
  avg_rating?: number | null | undefined;
}

// Case 360° (Stage 14) — the Team tab read model (GET /matters/:id/team).
// Server-owned structure so the members display can evolve independently.
export type MatterTeamRole = 'PARTNER' | 'LEAD' | 'STAFF';

export interface MatterTeamMember {
  id: number;
  name?: string | null | undefined;
  email?: string | null | undefined;
  /** Matter-level role (Accountable / Lead / Contributor). */
  role: MatterTeamRole;
  /** Global role type (MD / PARTNER / LEAD / STAFF / MANAGER). */
  roleType?: string | null | undefined;
  roleName?: string | null | undefined;
  /** Active original tasks on this matter (not DONE/BILLED). */
  activeTasks: number;
  /** Total tracked hours on this matter. */
  hours?: number | undefined;
  /** Tracked hours sitting in PARTNER_PENDING tasks on this matter. */
  pendingHours?: number | undefined;
  /** Billable share (%) of the member's own tracked time on this matter. */
  utilization?: number | undefined;
  /** Post-close K3 review score (1–5) on this matter, null when not yet rated. */
  matterRating?: number | null | undefined;
}

// ── Matter QA scoring (K1 source, TS Lot 1 §5.2 / §6.3) ──────────────────────
// Reviewer assessment against the MATTER criteria matrix on closed matters.
// Distinct from the K3 manager review above: K1 is a reviewer's per-criterion
// quality assessment, K3 is a single overall manager rating.

export interface QaCriterion {
  id: number;
  name: string;
  description?: string | null | undefined;
  /** Share of QA_Score this criterion carries; criteria weights sum to 1. */
  weight: number;
  type: 'MATTER' | 'PENDING_LEAD' | 'PENDING_PARTNER' | 'PENDING_BILLING';
}

/** One stored score (GET /matters/:id/qa). */
export interface MatterQaScore {
  id: number;
  matterId: number;
  ratedUserId: number;
  raterUserId: number;
  criteriaId: number;
  score: number; // 0–5
  comment?: string | null | undefined;
  createdAt: string;
  criteria?: Pick<QaCriterion, 'id' | 'name' | 'type' | 'weight'> | undefined;
  ratedUser?: { id: number; name?: string | null | undefined } | undefined;
  raterUser?: { id: number; name?: string | null | undefined } | undefined;
}

export interface SubmitMatterQaDto {
  scores: {
    ratedUserId: number;
    criteriaId: number;
    score: number;
    comment?: string | undefined;
  }[];
}

export interface MatterTeam {
  matterId: number;
  phase?: MatterPhase | undefined;
  members: MatterTeamMember[];
  pendingDelegation?: LeadDelegation | null | undefined;
}

export interface MatterClient {
  id: number;
  name: string;
  lastname?: string | undefined;
  middlename?: string | undefined;
  email?: string | undefined;
  phone?: string | undefined;
  type?: 'PERSON' | 'COMPANY' | undefined;
  street?: string | undefined;
  city?: string | undefined;
  country?: string | undefined;
  zip?: string | undefined;
}

export interface MatterContract {
  id: number;
  name: string;
  type?: string | undefined;
  status?: string | undefined;
  /** Contract currency — money on the matter (budget, billing) is in this. */
  currency?: 'USD' | 'UZS' | undefined;
  /** Billing cycle — 'INSTANCE' unlocks the Case Instances tab. */
  periodType?:
    | 'WEEK'
    | 'MONTH'
    | 'QUARTER'
    | 'HALF_YEAR'
    | 'YEAR'
    | 'INSTANCE'
    | 'MANUAL'
    | undefined;
  startsAt?: string | undefined;
  endsAt?: string | undefined;
  client?: MatterClient | undefined;
}

export interface MatterPracticeArea {
  id: number;
  name: string;
}

export interface Matter {
  id: number;
  name?: string | undefined;
  description?: string | undefined;
  phase?: MatterPhase | undefined;
  category?: MatterCategory | undefined;
  result?: MatterResult | undefined;
  nps?: number | null | undefined;
  isCourtCase: boolean;
  // NOTE: on the LIST endpoint `riskLevel` is a computed lost-time % (number);
  // on the DETAIL endpoint it is the Stage 6 Risk enum. The Compliance UI reads
  // the dedicated `GET :id/compliance` endpoint instead of this field.
  riskLevel?: string | undefined;
  // Case 360° (Lot 2) Stage 6 — present on the detail endpoint.
  compliance?: ComplianceStatus | undefined;
  complianceNote?: string | null | undefined;

  // Case 360° (Lot 2) Stage 4 — Quick Mode (only set for category = QA).
  quickDeadline?: string | null | undefined;
  quickExtensions?: number | undefined;

  // Case 360° (Lot 2) Stage 5 — Emergency Mode (PROVISIONAL_OPEN) clock.
  emergencyOpenedAt?: string | null | undefined;
  emergencyExpiresAt?: string | null | undefined;
  emergencyExtensions?: number | undefined;
  closureType?: ClosureType | undefined;
  closureReason?: string | null | undefined;

  // Case 360° (Lot 2) Stage 13 — billing posture derived from `phase` on the
  // detail endpoint (never stored): allowed | tracking_only | readonly.
  billingStatus?: BillingStatus | undefined;

  // Case 360° (Lot 2) Stage 11 — Matter Plan (CR targets + soft-check flags).
  scopeIn?: string | null | undefined;
  scopeOut?: string | null | undefined;
  estimate?: number | null | undefined;
  budget?: number | null | undefined;
  targetEndDate?: string | null | undefined;
  matterPlanConfirmed?: boolean | undefined;
  feedbackRequested?: boolean | undefined;

  // Case details
  caseNumber?: string | undefined;
  plainTiff?: string | undefined;
  defendant?: string | undefined;
  clientStatus?: ClientStatus | undefined;

  // Court information
  judgeName?: string | undefined;
  courtName?: string | undefined;
  courtEmail?: string | undefined;
  courtPhone?: string | undefined;

  // Relations
  contractId: number;
  contract?: MatterContract | undefined;
  clientId?: number | undefined;
  client?: MatterClient | undefined;
  practiceAreaId?: number | undefined;
  practiceArea?: MatterPracticeArea | undefined;
  responsibleAttorneys?: MatterUser[] | undefined;
  staffs?: MatterUser[] | undefined;
  partners?: MatterUser[] | undefined;

  // Timestamps
  createdAt: string;
  updatedAt?: string | undefined;
  endedAt?: string | null | undefined;
}

export interface CreateMatterDto {
  isCourtCase: boolean;
  category: Exclude<MatterCategory, 'MIGRATION_PENDING'>;
  description?: string | undefined;
  caseNumber?: string | undefined;
  plainTiff?: string | undefined;
  defendant?: string | undefined;
  judgeName?: string | undefined;
  courtName?: string | undefined;
  courtEmail?: string | undefined;
  courtPhone?: string | undefined;
  clientStatus?: ClientStatus | undefined;
  contractId: number;
  practiceAreaId?: number | undefined;
  // Case 360° — optional Matter Plan + initial risk at creation (2026-06-07).
  scopeIn?: string | undefined;
  scopeOut?: string | undefined;
  estimate?: number | undefined;
  budget?: number | undefined;
  targetEndDate?: string | undefined;
  riskLevel?: RiskLevel | undefined;
  // MD-managed case — when true, partner/lead/staff are all optional.
  isMdManaged?: boolean | undefined;
  responsibleAttorneys?: number[] | undefined;
  staffs?: number[] | undefined;
  partners?: number[] | undefined;
}

export interface UpdateMatterDto extends Partial<CreateMatterDto> {
  id?: number | undefined;
}

// Case 360° (Lot 2) Stage 4 — Quick Advice 3-click create.
export interface CreateQuickMatterDto {
  topic: string;
  contractId: number;
  responsiblePartnerId: number;
}

export interface MatterFilters {
  s?: string | undefined;
  limit?: number | undefined;
  page?: number | undefined;
  offset?: number | undefined;
  orderBy?: string | undefined;
  order?: 'asc' | 'desc' | undefined;
  'dueAt.min'?: string | undefined;
  'dueAt.max'?: string | undefined;
  phase?: MatterPhase | undefined;
  /** Comma-separated phases for multi-phase filtering (e.g. dashboard drill-downs). */
  phases?: string | undefined;
  result?: MatterResult | undefined;
  compliance?: ComplianceStatus | undefined;
  clientId?: number | undefined;
  userId?: number | undefined;
  contractId?: number | undefined;
  practiceAreaId?: number | undefined;
  isCourtCase?: boolean | undefined;
}

export interface MatterListResponse {
  results: Matter[];
  total: number;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface CloseMatterDto {
  result: MatterResult;
}


